/**
 * What the inline unlock card should say, derived from the user's saved keys.
 *
 * Three states, because "add a key" is the wrong instruction for a user who
 * already added one and whose key has since stopped working:
 *
 * - `loading`  — the key list or the capable-provider list is still in flight
 * - `none`     — no capable provider has a stored key
 * - `failing`  — a capable provider has a stored key whose last check failed
 *
 * The provider's own error text (`last_test_error`) is deliberately never read
 * here: it can echo the key back. See `.claude/rules/byok-key-handling.md` §2.
 *
 * @module ai_services/hooks/use-byok-gate-state
 */

import { useByokFeatureProviders } from "@shared/hooks/use-paywall";
import { useByokKeys } from "./use-byok";
import type { ByokProvider } from "../api";

export type ByokGateState =
  | { kind: "loading" }
  | { kind: "none"; providers: ByokProvider[] }
  | {
      kind: "failing";
      provider: ByokProvider;
      lastTestedAt: string | null;
      providers: ByokProvider[];
    };

export function useByokGateState(featureKey: string): ByokGateState {
  const { providers, isLoading: providersLoading } =
    useByokFeatureProviders(featureKey);
  const { data, isLoading: keysLoading } = useByokKeys();

  const capable = providers as ByokProvider[];

  if (providersLoading || (keysLoading && data === undefined)) {
    return { kind: "loading" };
  }

  const rows = data?.providers ?? [];
  // Capability-map order is the order the server would try them in, so the
  // first failing row is the one the user most needs to fix.
  for (const provider of capable) {
    const row = rows.find((candidate) => candidate.provider === provider);
    if (row?.has_key && row.last_test_status === "failed") {
      return {
        kind: "failing",
        provider,
        lastTestedAt: row.last_tested_at ?? null,
        providers: capable,
      };
    }
  }
  return { kind: "none", providers: capable };
}
